import React from 'react';
import './Input.css';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input label */
  label?: string;
  /** Helper text below input */
  helperText?: string;
  /** Error message (shows error state) */
  error?: string;
  /** Full width input */
  fullWidth?: boolean;
  /** Size variant (sm: 24px, md: 30px, lg: 40px) */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS class */
  className?: string;
}

/**
 * Input component for text entry.
 * Inspired by Blueprint design system with compact, professional styling.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { label, helperText, error, fullWidth = false, size = 'md', className = '', id, ...props },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;

    const containerClasses = [
      'stc-input-container',
      fullWidth && 'stc-input-container--full-width',
      hasError && 'stc-input-container--error',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputClasses = ['stc-input', size !== 'md' && `stc-input--${size}`]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={inputId} className="stc-input__label">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          aria-invalid={hasError}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />
        {error && (
          <span id={`${inputId}-error`} className="stc-input__error" role="alert">
            {error}
          </span>
        )}
        {!error && helperText && (
          <span id={`${inputId}-helper`} className="stc-input__helper">
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

/* Textarea variant */

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Textarea label */
  label?: string;
  /** Helper text below textarea */
  helperText?: string;
  /** Error message (shows error state) */
  error?: string;
  /** Full width textarea */
  fullWidth?: boolean;
  /** Additional CSS class */
  className?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, helperText, error, fullWidth = false, className = '', id, ...props }, ref) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;

    const containerClasses = [
      'stc-input-container',
      fullWidth && 'stc-input-container--full-width',
      hasError && 'stc-input-container--error',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={textareaId} className="stc-input__label">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className="stc-input stc-textarea"
          aria-invalid={hasError}
          aria-describedby={
            error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
          }
          {...props}
        />
        {error && (
          <span id={`${textareaId}-error`} className="stc-input__error" role="alert">
            {error}
          </span>
        )}
        {!error && helperText && (
          <span id={`${textareaId}-helper`} className="stc-input__helper">
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
